import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import classNames from 'classnames';
import config from "config";

import css from "./style.scss";
import css2 from '../style.scss';

import {
  selectPrimaryUserId,
  selectCustomerNumber,
  selectMarketingProfile,
  selectProfile,
} from "data/user/selectors";

import {
  getMarketingProfile
} from "data/user/actions";

import {
  getContactIds,
  getAppendJobEstimate
} from "data/contacts/actions";

import {
  selectAssignableGroups,
  selectAppendGroups
} from "data/contacts/selectors";

import {
  selectCampaign,
  selectDirectMailCampaigns,
  selectDirectMailCampaignBallpointAPIKey,
} from "data/campaigns/selectors";

import {
  CampaignTypes,
  DirectMailBallpointOrderStatus,
  DirectMailCampaignOrderRecipientUploadStatus,
} from "data/campaigns/constants";

import {
  createDirectMailCampaign,
  getDirectMailCampaign,
  getDirectMailCampaignBallpointAPIKey,
  getDirectMailCampaignByBallpointCampaignId,
  getDirectMailCampaignBallpointOrder,
  createDirectMailCampaignOrder,
  confirmDirectMailCampaignSubmission,
  updateDirectMailCampaign,
  updateDirectMailCampaignBallpointRecipients,
  updateDirectMailCampaignOrder,
  cancelDirectMailCampaignOrder,
  getDirectMailCampaignsByMarketingCampaignId,
  getDirectMailPieceCounts,
  saveDirectMailCampaignRts,
  getDirectMailCostPreview,
  suppressDirectMailRtsContacts,
  updateDirectMailContactOptOuts
} from "data/campaigns/actions";

import { useToast } from "app/components/ToastMessage/ToastManager";
import { ContactAssignmentPopup } from "app/components/GroupAssignment";
import AddToMarketingListModal from "app/components/AddToMarketingListModal";
import SkipTraceModal from "app/components/SkipTraceModal";
import InfoAlert from "app/components/InfoAlert";
import CreateDirectMailHandler from "../Dashboard/DirectMail/CreateDirectMailHandler";
import EditMarketingProfilePopup from "../../AccountNew/modals/EditMarketingProfile";

import IFrameMessageHandler from "components/IFrameMessageHandler";
import CampaginPaymentModal from "../../components/CampaginPaymentModal";

import LoaderModal from "app/components/LoaderModal";

const {
  constants: {
    API: { BALLPOINT_API_BASE_URL, BALLPOINT_IFRAME_URL },
  },
} = config;

// Guards against duplicate mounts of this component (overlapping route matches can
// currently render it more than once for the same URL) ending up with two instances
// each creating their own IFrameMessageHandler/iframe and independently processing
// every message from Ballpoint. Stopgap: only the first instance to render becomes
// active; any other stays inert for its whole lifetime (it does not retroactively
// activate). A new instance mounted after the active one unmounts can claim the slot
// instead. The real fix is to stop the routing from matching the same URL twice.
let _activeDirectMailInstance = null;

const DirectMailCampaign = ({
  location,
  history,
  openContactAssignmentPopup,
  openAddToMarketingListModal,
  openSkipTraceModal,
  openInfoAlert,
  EditMarketingProfilePopup,
  closeEditMarketingProfilePopup
}) => {
  const dispatch = useDispatch();
  const showToast = useToast();

  const instanceRef = useRef(null);
  if (instanceRef.current === null) {
    instanceRef.current = {};
  }

  const [isActiveInstance] = useState(() => {
    if (_activeDirectMailInstance === null) {
      _activeDirectMailInstance = instanceRef.current;
      return true;
    }

    return false;
  });

  useEffect(() => {
    return () => {
      if (_activeDirectMailInstance === instanceRef.current) {
        _activeDirectMailInstance = null;
      }
    };
  }, []);

  const iFrameId = "ballpoint-direct-mail";
  const IFRAME_MESSAGE_SOURCE = "ballpoint-mailer";
  const SET_API_CONFIG = "set_api_config";
  const SET_LIST = "set_list";
  const SET_DASHBOARD_FILTER = "set_dashboard_filter";
  const OPEN_DIRECT_MAIL_DASHBOARD = "open_direct_mail_dashboard";
  const SET_SENDER = "set_sender";
  const OPEN_CREATE_DIRECT_MAIL = "open_create_direct_mail";
  const RECIPIENT_OPT_OUT_CHANGED = "recipient_opt_out_changed";

  const READY = "ready";
  const CAMPAIGN_HOME_REQUESTED = "campaign_home_requested";
  const OPEN_CREATE_DIRECT_MAIL_FAILED = "open_create_direct_mail_failed";
  const CREATE_DIRECT_MAIL_REQUESTED = "create_direct_mail_requested";
  const CAMPAIGN_CREATED = "campaign_created";
  const EDIT_LEADS_REQUESTED = "edit_leads_requested";
  const RECIPIENTS_UPDATED = "recipients_updated";
  const SENDER_SETUP_REQUESTED = "sender_setup_requested";
  const CAMPAIGN_SUBMITTED = "campaign_submitted";
  const ORDER_RESCHEDULED = "order_rescheduled";
  const ORDER_CANCELLED = "order_cancelled";
  const DOWNLOAD_CSV = "download_csv";
  const ADD_TO_MARKETING_LIST_REQUESTED = "add_to_marketing_list_requested";
  const PAYMENT_RETRY_REQUESTED = "payment_retry_requested";
  const MAILING_ADDRESS = "MAILING_ADDRESS";
  const PROPERTY_ADDRESS = "PROPERTY_ADDRESS";
  const AUTO_SUPPRESS_NEXT_DROP_CHANGED = "auto_suppress_next_drop_changed";
  const BOTH = "BOTH";


  const primaryUserId = useSelector(selectPrimaryUserId);
  const customerNumber = useSelector(selectCustomerNumber);
  const currentMarketingCampaignRaw = useSelector(selectCampaign);
  const currentMarketingCampaign = useMemo(() => currentMarketingCampaignRaw?.toJS(), [currentMarketingCampaignRaw]);
  const marketingProfileRaw = useSelector(selectMarketingProfile);
  const marketingProfile = useMemo(() => marketingProfileRaw?.toJS(), [marketingProfileRaw]);
  const ballpointAPIKey = useSelector(selectDirectMailCampaignBallpointAPIKey);
  const profileRaw = useSelector(selectProfile);
  const profile = useMemo(() => profileRaw?.toJS(), [profileRaw]);
  const assignableGroupsRaw = useSelector(selectAssignableGroups);
  const assignableGroups = useMemo(() => assignableGroupsRaw?.toJS(), [assignableGroupsRaw]);
  const appendGroupsRaw = useSelector(selectAppendGroups);
  const appendGroups = useMemo(() => appendGroupsRaw?.toJS(), [appendGroupsRaw]);

  const currentMarketingCampaignRef = useRef(null);
  const marketingProfileRef = useRef(null);
  const iFrameMessageHandlerRef = useRef(null);
  const createdCampaignIdRef = useRef(null);
  const processedCampaignIdRef = useRef(null);


  const [isIFrameReady, setIsIFrameReady] = useState(false);

  const [iframeKey, setIframeKey] = useState(0);
  const [pendingNewCampaign, setPendingNewCampaign] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const loaderTimeoutRef = useRef(null);

  // Safety net for the CREATE_DIRECT_MAIL_REQUESTED loader: the flow it waits on spans an
  // iframe remount and a 'ready' postMessage the iframe controls, so if the iframe never loads
  // (script/network failure, crash before init) neither the success path nor the
  // OPEN_CREATE_DIRECT_MAIL_FAILED message will ever fire to clear it.
  const CREATE_DIRECT_MAIL_LOADER_TIMEOUT_MS = 20000;

  const startLoader = () => {
    setShowLoader(true);
    clearTimeout(loaderTimeoutRef.current);
    loaderTimeoutRef.current = setTimeout(() => {
      console.error("[Direct Mail] Timed out waiting for the Create Direct Mail page to load");
      setShowLoader(false);
    }, CREATE_DIRECT_MAIL_LOADER_TIMEOUT_MS);
  };

  const stopLoader = () => {
    clearTimeout(loaderTimeoutRef.current);
    setShowLoader(false);
  };

  useEffect(() => () => clearTimeout(loaderTimeoutRef.current), []);

  const directMailCampaigns = useSelector(selectDirectMailCampaigns);

  // True when landing on the create-campaign iframe flow: either navigated here with
  // `isNewCampaign` state, or a new campaign was just created via CREATE_DIRECT_MAIL_REQUESTED
  // (pendingNewCampaignId set below).
  const openCreateCampaignIFramePage = !!location?.state?.isNewCampaign || !!pendingNewCampaign;

  const campaignForCreateCampaignPage = location?.state?.newCampaign || pendingNewCampaign;

  const isSenderSetupPendingRef = useRef(false);

  useEffect(() => {
    if (!isActiveInstance) return;

    iFrameMessageHandlerRef.current = new IFrameMessageHandler(
      iFrameId, BALLPOINT_IFRAME_URL, IFRAME_MESSAGE_SOURCE, { source: 'propstream', version: 1 }
    );
    return () => {
      iFrameMessageHandlerRef.current?.destroy();
    };
  }, [isActiveInstance]);

  // Re-resolves the handler's iFrame element reference once the new <iframe> (remounted
  // via the `key` change below) has actually landed in the DOM.
  useEffect(() => {
    iFrameMessageHandlerRef.current?.reset();
  }, [iframeKey]);

  useEffect(() => {
    if (primaryUserId) {
      dispatch(getDirectMailCampaignBallpointAPIKey());
      dispatch(getMarketingProfile(primaryUserId));
    }
  }, [primaryUserId, dispatch]);

  useEffect(() => {
    if (!currentMarketingCampaign?.id) return;

    currentMarketingCampaignRef.current = currentMarketingCampaign;

    dispatch(getDirectMailCampaignsByMarketingCampaignId(currentMarketingCampaign.id));
  }, [currentMarketingCampaign.id]);

  useEffect(() => {
    if (!marketingProfile || Object.keys(marketingProfile).length === 0) return;

    marketingProfileRef.current = marketingProfile;
  }, [marketingProfile]);

  /**
   * Updates a Direct Mail campaign's Ballpoint campaign association, name, and type.
   *
   * Called when the Ballpoint iFrame fires a `campaign_created` or `edit_leads_requested` event.
   * Dispatches `updateDirectMailCampaign` with:
   *   - the Ballpoint campaign ID to associate with the local campaign
   *   - the campaign name from the iFrame message (via the `listName` field)
   *   - the campaign type from the iFrame message, uppercased
   *
   * @param {string} campaignId - The local Direct Mail campaign ID (corresponds to `listId` in the iFrame message).
   * @param {string} ballpointCampaignId - The Ballpoint-side campaign ID to link to the local campaign.
   * @param {string|null} campaignName - The campaign name from the iFrame message (passed via the `listName` field).
   * @param {string} campaignType - The campaign type from the iFrame message; uppercased before persisting.
   */
  const handleUpdateDirectMailCampaign = async (campaignId, ballpointCampaignId, campaignName, campaignType) => {
    return await dispatch(updateDirectMailCampaign(campaignId, {
      ballpointCampaignId: ballpointCampaignId,
      name: campaignName,
      campaignType: campaignType.toUpperCase(),
    }));
  }

  /**
   * Opens the contact assignment popup for the given Direct Mail campaign, allowing the user
   * to edit the campaign's lead list.
   *
   * Fetches the current contact IDs for the campaign's contact group, then opens the
   * ContactAssignmentPopup. On save:
   *   1. Dispatches `updateDirectMailCampaign` with the updated contact IDs.
   *   2. Fetches the updated piece counts for the campaign's (possibly new) contact group.
   *   3. Sends a `set_list` message to the Ballpoint iFrame with the campaign's new total
   *      lead count and piece counts, so the iFrame's UI reflects the edited list.
   *
   * @param {Object} directMailCampaign - The Direct Mail campaign whose leads should be edited.
   * @param {number} directMailCampaign.id - The campaign ID used for the update call.
   * @param {number} directMailCampaign.contactGroupId - The contact group ID used to fetch current contacts.
   */
  const handleEditLeadsRequested = async (directMailCampaign) => {
    dispatch(getContactIds("C" + directMailCampaign.contactGroupId, ({ response }) => {
      openContactAssignmentPopup({
        ids: response,
        onSave: async (updatedContactIds) => {
          const updatedCampaign = await dispatch(
            updateDirectMailCampaign(directMailCampaign.id, {
              contactIds: updatedContactIds,
            })
          );

          const campaignPieceCounts = await dispatch(getDirectMailPieceCounts(updatedCampaign.contactGroupId));

          iFrameMessageHandlerRef.current.sendMessage(SET_LIST, {
            listId: String(updatedCampaign.id),
            count: updatedCampaign.totalLeadsCount,
            piece_counts: campaignPieceCounts,
          });
        },
      });
    }));
  };

  /**
   * Opens the contact assignment popup for a Direct Mail campaign whose `edit_leads_requested`
   * event came from a post-creation scope (`single_send`, `multi_month_campaign`, `ab_split` —
   * i.e. a campaign card in My Campaigns, as opposed to `creation_flow`, which is handled by
   * `handleEditLeadsRequested` above and never reaches this function).
   *
   * Fetches the current contact IDs for the campaign's contact group (the "before" snapshot),
   * then opens the same ContactAssignmentPopup used by the creation flow. On save:
   *   1. Dispatches `updateDirectMailCampaign` with the updated ("after") contact IDs.
   *   2. Diffs the before/after contact ID lists into `contactIdsAdded` / `contactIdsRemoved`
   *      and dispatches `updateDirectMailCampaignBallpointRecipients` so the linked Ballpoint
   *      campaign's recipients stay in sync. Our backend owns translating this flat delta into
   *      whatever Ballpoint-side calls are needed (campaign-level delta, per-order, A/B-split
   *      allocation) — the frontend never calls Ballpoint's API directly.
   *   3. Only if that sync succeeds, sends `recipients_updated` back to the iFrame so it
   *      refreshes the My Campaigns card for the affected orders, using the `ballpointOrderId`
   *      of each order in the sync response's `dropsAffected`. If the sync fails, the error
   *      is swallowed — it must not surface a blocking error to the user — and
   *      `recipients_updated` is skipped for this save. (TODO: currently not logged or toasted.)
   *
   * @param {Object} directMailCampaign - The Direct Mail campaign whose leads should be edited.
   * @param {number} directMailCampaign.id - The campaign ID used for the update call.
   * @param {number} directMailCampaign.contactGroupId - The contact group ID used to fetch current contacts.
   * @param {string} directMailCampaign.ballpointCampaignId - The linked Ballpoint campaign ID.
   * @param {string|null} iframeCampaignId - Iframe-local campaign handle (`message.campaignId`), echoed back on `recipients_updated`.
   * @param {string} campaignDeltaEndpoint - The Ballpoint-side endpoint hint (`message.campaignDeltaEndpoint`) forwarded to `updateDirectMailCampaignBallpointRecipients` so the backend knows which delta call to make.
   */
  const handlePostCreationEditLeadsRequested = async (directMailCampaign, iframeCampaignId, campaignDeltaEndpoint) => {
    dispatch(getContactIds("C" + directMailCampaign.contactGroupId, ({ response }) => {
      const previousContactIds = response;

      openContactAssignmentPopup({
        ids: previousContactIds,
        onSave: async (updatedContactIds) => {
          const contactIdsAdded = updatedContactIds.filter((id) => !previousContactIds.includes(id));
          const contactIdsRemoved = previousContactIds.filter((id) => !updatedContactIds.includes(id));

          try {
            const { dropsAffected } = await dispatch(
              updateDirectMailCampaignBallpointRecipients(
                directMailCampaign.ballpointCampaignId,
                contactIdsAdded,
                contactIdsRemoved,
                campaignDeltaEndpoint,
              )
            );

            const affectedBallpointOrderIds = (dropsAffected || []).map((drop) => drop.ballpointOrderId);

            if (affectedBallpointOrderIds.length == 0) return;
            
            await dispatch(
              updateDirectMailCampaign(directMailCampaign.id, {
                contactIds: updatedContactIds,
              })
            );

            iFrameMessageHandlerRef.current.sendMessage(RECIPIENTS_UPDATED, {
              tenantKey: String(primaryUserId),
              campaignId: iframeCampaignId,
              ballpointCampaignId: directMailCampaign.ballpointCampaignId,
              updatedBallpointOrderIds: affectedBallpointOrderIds,
            });
          } catch (e) {
            // TODO: Show error toast, or log error
          }
        },
      });
    }));
  };

  /**
   * Kicks off a Skip Trace append job for the given contact ids and opens the Skip Trace
   * modal on success, or an "already in progress" alert if one is already running for
   * this account. Mirrors the pattern used for property-originated skip traces (e.g.
   * Search, withProperty), but operates directly on contact ids since the RTS suppression
   * entries are already resolved contacts — no property-to-contact lookup is needed.
   *
   * @param {number[]} contactIds
   */
  const handleSkipTraceContacts = (contactIds) => {
    if (!contactIds.length) return;

    dispatch(getAppendJobEstimate({ contactIds, appendMode: 'BOTH', reprocessEnabled: false }, ({ response, error }) => {
      if (error) {
        console.error('Error fetching skip trace estimate:', error);
        return;
      }

      if (response?.message?.includes('There is an existing append job')) {
        openInfoAlert({
          title: 'Skip Trace in Progress',
          infoText: 'There is a Skip Trace currently in progress. Please try again once it is complete.',
        });
      } else {
        openSkipTraceModal({
          groups: appendGroups,
          reprocessEnabled: false,
          contactIds,
          selectedProperties: contactIds.length,
        });
      }
    }));
  };

  /**
   * Opens the Add to Marketing List modal in response to the Ballpoint iFrame's
   * `add_to_marketing_list_requested` event, letting the user pick an existing Contact Group
   * or create a new one, then assigns the Direct Mail campaign's RTS contacts to
   * that contact group via the Direct Mail RTS endpoint. If the user checks Skip Trace,
   * the same recipients are queued into the skip trace workflow after the save succeeds.
   *
   * @param {string} ballpointCampaignId - The Ballpoint campaign ID from the iFrame message.
   * @param {Array<{contact_id: string, contact_type: string|null}>} [recipients] - RTS
   *   suppression entries the iFrame handed over, per the `add_to_marketing_list_requested`
   *   contract. Only entries carrying a `contact_id` are usable for skip tracing.
   */
  const handleCreateMarketingList = (ballpointCampaignId, recipients = []) => {
    openAddToMarketingListModal({
      title: 'Add to Marketing List',
      checkboxLabel: 'Skip Trace Selected Recipients',
      groups: assignableGroups,
      onSave: (name, onComplete, saveOptions) => {
        // Contact group ids carry a type-prefix letter (e.g. "C1234") — strip it, the RTS endpoint wants the bare number.
        const contactGroupId = saveOptions?.id ? Number(String(saveOptions.id).replace(/\D/g, '')) || null : null;

        dispatch(saveDirectMailCampaignRts(name, ballpointCampaignId, contactGroupId, ({ _ }) => {
          onComplete();

          if (saveOptions?.skipTrace) {
            const contactIds = recipients
              .map((recipient) => Number(recipient?.contact_id))
              .filter((contactId) => Number.isFinite(contactId));

            handleSkipTraceContacts(contactIds);
          }
        }));
      },
    });
  };

  // Ballpoint sends this value in different cases per message ("mailing" in
  // recipient_selection, "MAILING" in recipient_opt_out_changed), so match case-insensitively.
  const getDeliverToValue = (deliverTo) => {
    const normalizedDeliverTo = String(deliverTo ?? "").trim().toLowerCase();

    if (normalizedDeliverTo === "mailing") return MAILING_ADDRESS;
    if (normalizedDeliverTo === "property") return PROPERTY_ADDRESS;
    if (normalizedDeliverTo === "both") return BOTH;
    return null;
  };

  /**
   * Syncs the opt-out state of the recipients the iFrame reports in a single
   * `recipient_opt_out_changed` event.
   *
   * The whole batch goes to the backend in ONE request — the endpoint takes an array of
   * entries — rather than one request per recipient. Recipients missing a usable contact id
   * or carrying an address type we can't map are dropped from the batch (and logged) instead
   * of failing the request for the rest; if nothing is usable, no call is made at all.
   *
   * On failure the user is toasted: the iFrame has already flipped the toggle in its own UI,
   * so a silent failure would leave them believing the change was saved when it wasn't.
   *
   * Ballpoint's `contact_type` is the raw address target ("MAILING", "mailing", ...), so it is
   * normalized through `getDeliverToValue` into the address enum value the backend expects.
   *
   * @param {Object} message - The `recipient_opt_out_changed` iFrame message.
   * @param {boolean} message.opted_out - The new opt-out state to apply to every recipient in the batch.
   * @param {Array<{ contact_id: string|number, contact_type: string|null }>} [message.recipients] - Affected recipients.
   */
  const handleRecipientOptOutChanged = async ({ opted_out, recipients = [] }) => {
    const isOptedOut = !!opted_out;

    const contactOptOuts = recipients.reduce((batch, recipient) => {
      const contactId = Number(recipient?.contact_id);
      const addressType = getDeliverToValue(recipient?.contact_type);

      if (!Number.isFinite(contactId) || !addressType) {
        console.warn(
          "[Direct Mail] recipient_opt_out_changed skipped recipient with a missing contact id or unrecognized contact type",
          recipient
        );
        return batch;
      }

      batch.push({ contactId, addressType, isOptedOut });
      return batch;
    }, []);

    if (!contactOptOuts.length) return;

    try {
      await dispatch(updateDirectMailContactOptOuts(contactOptOuts));
    } catch (error) {
      console.error("[Direct Mail] Failed to sync recipient opt-outs", error);

      showToast(
        `Something went wrong and the opt-out ${contactOptOuts.length > 1 ? "changes were" : "change was"} not saved. Please try again.`,
        "error"
      );
    }
  };

  /**
   * Persists a DirectMailCampaignOrder record for each order returned by the Ballpoint iframe
   * after the user completes checkout.
   *
   * Orders are processed sequentially — each order fully completes before the next starts,
   * preventing race conditions. For each order:
   *   1. Fetches the full Ballpoint order (pricing, product, postage details) by ballpointOrderId.
   *   2. Fetches the local Direct Mail campaign linked to that Ballpoint campaign via campaignId.
   *   3. Updates the Direct Mail campaign with recipient selection settings (deliverTo,
   *      removeDuplicateMailingAddresses) if recipientSelection is provided.
   *   4. Creates a DirectMailCampaignOrder joining the iframe order, Ballpoint order, and local
   *      campaign — capturing all details at the moment of submission.
   *
   * After the loop, a single confirm-submission call is made to the backend with the
   * Ballpoint order IDs of all submitted orders, which triggers the upload of the
   * campaign's recipients to Ballpoint for those orders.
   *
   * Both action creators return their fetched data directly (thunk/Promise pattern), so results
   * are used as local variables rather than relying on Redux state, avoiding stale closure issues.
   *
   * Errors are caught per-order so a single failure does not block remaining orders.
   *
   * @param {Array<{ orderId: string, ballpointOrderId: string, mailDate: string }>} orders - Orders from the iframe CAMPAIGN_SUBMITTED message. Each order includes its own scheduled mail date (ISO 8601).
   * @param {string} ballpointCampaignId - The Ballpoint campaign ID used to look up the linked local Direct Mail campaign.
   * @param {Object|null} recipientSelection - Recipient selection settings from the iframe message;
   *   used to update the campaign's `deliverTo` and `removeDuplicateMailingAddresses` fields before creating orders.
   */
  const handleCampaignSubmitted = async (orders, ballpointCampaignId, directMailCampaign, recipientSelection) => {
    // Update Direct Mail Campaign with recipient selection values
    if (
      recipientSelection &&
      directMailCampaign?.id
    ) {
      await dispatch(
        updateDirectMailCampaign(directMailCampaign.id, {
          deliverTo: getDeliverToValue(recipientSelection.deliver_to),
          removeDuplicateMailingAddresses:
            recipientSelection.remove_duplicate_addresses,
        })
      );
    }

    for (const [index, order] of orders.entries()) {
      try {
        const ballpointOrder = await dispatch(
          getDirectMailCampaignBallpointOrder(order.ballpointOrderId)
        );

        await dispatch(
          createDirectMailCampaignOrder({
            directMailCampaignId: directMailCampaign.id,
            contactGroupId: directMailCampaign.contactGroupId,
            orderIndex: index,
            iframeOrderId: order.orderId,
            ballpointOrderId: ballpointOrder.id,
            productType: ballpointOrder.productType,
            postageType: ballpointOrder.postageType,
            envelopeStyle: ballpointOrder.envelopeStyle,
            scheduledMailDate: new Date(order.mailDate).getTime(),
            pieceCount: ballpointOrder.pieceCount,
            unitPriceTcents: order.unit_price_tcents,
            totalPriceTcents: order.total_tcents,
            status: DirectMailBallpointOrderStatus.SUBMITTED,
            recipientsUploadStatus:
              DirectMailCampaignOrderRecipientUploadStatus.PENDING,
            campaignType: directMailCampaign.campaignType
          })
        );
      } catch (e) {
        // TODO: Show error toast, or log error
        return false;
      }
    }

    // Confirm the submitted orders to the backend so it uploads the
    // campaign's recipients to Ballpoint for those orders.
    if (orders.length > 0) {
      try {
        const submissionResult = await dispatch(
          confirmDirectMailCampaignSubmission(
            ballpointCampaignId,
            orders.map((order) => order.ballpointOrderId)
          )
        );

        return submissionResult.orders.every((order) => order.ready);
      } catch (e) {
        // TODO: Show error toast, or log error
        return false;
      }
    }

    return false;
  };

  /**
   * Triggers a CSV file download in the browser using a temporary object URL.
   *
   * Creates a Blob from the provided content, attaches it to a temporary anchor element,
   * appends the anchor to the DOM (required for Firefox compatibility), clicks it to
   * initiate the download, then removes the anchor. The object URL is revoked after a
   * short delay to ensure the browser has had time to start the download before the
   * resource is released.
   *
   * Called when the Ballpoint iFrame fires a `download_csv` event.
   *
   * @param {string} filename  - The name to give the downloaded file (e.g. "leads.csv").
   * @param {string} content   - The raw file content to download.
   * @param {string} mimeType  - The MIME type of the file (e.g. "text/csv").
   */
  const handleCSVDownload = (filename, content, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  // Handling received IFrame postMessage events/messages
  useEffect(() => {
    if (!isActiveInstance) return;

    iFrameMessageHandlerRef.current.subscribeToMessage(READY, (_) => {
      setIsIFrameReady(true);
    });

    if (currentMarketingCampaign?.id) {
      iFrameMessageHandlerRef.current.subscribeToMessage(CAMPAIGN_HOME_REQUESTED, (_) => {
        history.push(`/campaign/${currentMarketingCampaign.id}`);
      });
    }

    iFrameMessageHandlerRef.current.subscribeToMessage(OPEN_CREATE_DIRECT_MAIL_FAILED, (message) => {
      // The iframe couldn't open the Create Direct Mail page (e.g. after CREATE_DIRECT_MAIL_REQUESTED
      // remounts it) — the loader started at CREATE_DIRECT_MAIL_REQUESTED would otherwise never clear.
      stopLoader();

      if (openCreateCampaignIFramePage && message.reason === "list_context_missing") {
        // TODO: Show error toast: opening the Create Campaign page failed - Needs to be confirmed
      }
    });

    iFrameMessageHandlerRef.current.subscribeToMessage(EDIT_LEADS_REQUESTED, async (message) => {
      if (message.scope === "creation_flow") {
        const directMailCampaign = await dispatch(
          getDirectMailCampaign(message.listId)
        );
        
        if (!directMailCampaign) {
          // Toast error message
          return;
        }

        await handleEditLeadsRequested(directMailCampaign);
      } else {
        const directMailCampaign = await dispatch(
          getDirectMailCampaignByBallpointCampaignId(message.ballpointCampaignId)
        );

        if (!directMailCampaign) {
          // Toast error message
          return;
        }

        await handlePostCreationEditLeadsRequested(directMailCampaign, message.campaignId, message.campaignDeltaEndpoint);
      }
    });

    // Message for the 'campaign_created' event
    // Updates the Direct Mail Campaign's Ballpoint campaign association, name, and type
    iFrameMessageHandlerRef.current.subscribeToMessage(CAMPAIGN_CREATED, async (message) => {
      await handleUpdateDirectMailCampaign(message.listId, message.campaignId, message.listName, message.campaignType);

      dispatch(getDirectMailCampaignsByMarketingCampaignId(currentMarketingCampaignRef.current.id));
    });

    iFrameMessageHandlerRef.current.subscribeToMessage(
      CAMPAIGN_SUBMITTED,
      async (message) => {
        setShowPaymentModal(true);
        setIsPaymentLoading(true);

        try {
          if (processedCampaignIdRef.current === message.campaignId) {
            return;
          }

          processedCampaignIdRef.current = message.campaignId;

          const directMailCampaign = await dispatch(
            getDirectMailCampaignByBallpointCampaignId(message.campaignId)
          );

          const isSubmissionSuccessful = await handleCampaignSubmitted(
            message.orders,
            message.campaignId,
            directMailCampaign,
            message.recipient_selection
          );

          if (!isSubmissionSuccessful) {
            // TODO: Handle failed recipient upload
            return;
          }

          await dispatch(
            getDirectMailCostPreview(directMailCampaign.id)
          );
        } finally {
          setIsPaymentLoading(false);
        }
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(
      AUTO_SUPPRESS_NEXT_DROP_CHANGED,
      async (message) => {
        try {
          const response = await dispatch(
            suppressDirectMailRtsContacts(
              message.ballpointCampaignId,
              message.enabled
            )
          );

          const updatedBallpointOrderIds =
            (response.dropsAffected || []).map(
              drop => drop.ballpointOrderId
            );

          iFrameMessageHandlerRef.current.sendMessage(RECIPIENTS_UPDATED, {
            tenantKey: String(primaryUserId),
            campaignId: message.campaignId,
            ballpointCampaignId: message.ballpointCampaignId,
            updatedBallpointOrderIds,
          });
        } catch (e) {
          // TODO
        }
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(
      PAYMENT_RETRY_REQUESTED,
      async (_) => {
        setShowPaymentModal(true);
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(ORDER_RESCHEDULED, (message) => {
      // The 'updateDirectMailCampaignOrder' action method accepts 'orderId' or 'ballpointOrderId'.
      // In the current logic, we are calling it with 'ballpointOrderId', retrieved from the iframe message.
      dispatch(updateDirectMailCampaignOrder(null, message.orderId, { scheduledMailDate: new Date(message.newMailDate).getTime() }));
    });

    // Ballpoint has already cancelled the order server-side by the time this fires (the user
    // cancelled from inside the iframe). As with ORDER_RESCHEDULED above, 'orderId' and
    // 'campaignId' here are the Ballpoint-side order/campaign ids.
    iFrameMessageHandlerRef.current.subscribeToMessage(ORDER_CANCELLED, (message) => {
      dispatch(cancelDirectMailCampaignOrder(message.orderId));
    });

    iFrameMessageHandlerRef.current.subscribeToMessage(DOWNLOAD_CSV, (message) => {
      handleCSVDownload(message.filename, message.content, message.mime_type);
    });

    iFrameMessageHandlerRef.current.subscribeToMessage(
      SENDER_SETUP_REQUESTED,
      () => {
        isSenderSetupPendingRef.current = true;
        EditMarketingProfilePopup({
          onClose: () => {
            isSenderSetupPendingRef.current = false;
            closeEditMarketingProfilePopup();
          },
          onError: (error) => {
            isSenderSetupPendingRef.current = false;
            console.error("Error while updating the Marketing Profile: " + error);
            closeEditMarketingProfilePopup();
          },
        });
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(
      CREATE_DIRECT_MAIL_REQUESTED,
      async () => {
        try {
          startLoader();

          const handler = new CreateDirectMailHandler(
            primaryUserId,
            currentMarketingCampaignRef.current,
            marketingProfile,
            (payload) => dispatch(createDirectMailCampaign(payload)),
            (id) => dispatch(getMarketingProfile(id))
          );

          const newCampaign = await handler.run();

          // Clear the deep-link state now that `pendingNewCampaign` is the source of truth —
          // otherwise `location.state.isNewCampaign`/`newCampaign` (see openCreateCampaignIFramePage
          // and campaignForCreateCampaignPage above) would keep taking precedence over the campaign
          // just created here.
          if (location.state?.isNewCampaign || location.state?.newCampaign) {
            history.replace({ ...location, state: undefined });
          }

          setPendingNewCampaign(newCampaign);

          setIsIFrameReady(false);

          setIframeKey(prev => prev + 1);
        } catch (e) {
          console.error(e);
          stopLoader();
        }
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(
      RECIPIENT_OPT_OUT_CHANGED,
      (message) => {
        handleRecipientOptOutChanged(message);
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(ADD_TO_MARKETING_LIST_REQUESTED, (message) => {
      handleCreateMarketingList(message.ballpointCampaignId, message.recipients);
    });
  }, [currentMarketingCampaign, marketingProfile, assignableGroups, appendGroups, isActiveInstance]);

  const convertMarketingProfileToSenderSnapshotJson = (marketingProfile) => {
    return {
      fullName: marketingProfile.businessFullName,
      address: marketingProfile.businessFullStreetAddress ?? [marketingProfile.businessAddressLine1, marketingProfile.businessAddressLine2].filter(Boolean).join(", "),
      city: marketingProfile.businessCity,
      state: marketingProfile.businessState,
      zip: String(marketingProfile.businessZipCode),
      phone: marketingProfile.businessPhone,
      website: marketingProfile.businessWebsite,
      logo: marketingProfile.marketingProfilePicture,
    };
  }

  // Fetches piece counts for the given campaign, seeds the iframe with its list data via SET_LIST,
  // then triggers OPEN_CREATE_DIRECT_MAIL. Called only when an external access point pre-set
  // campaignIdForCreateCampaignPage before the iframe finished initializing.
  const openCreateCampaignFlow = async (campaign) => {
    createdCampaignIdRef.current = campaign.id;

    const campaignPieceCounts = await dispatch(getDirectMailPieceCounts(campaign.contactGroupId));

    iFrameMessageHandlerRef.current.sendMessage(SET_LIST, {
      count: campaign.totalLeadsCount,
      name: campaign.name,
      listId: String(campaign.id),
      externalUserId: String(primaryUserId),
      externalUserIsAccountOwner: profile.type === 'FULL',
      piece_counts: campaignPieceCounts,
      // Ballpoint requires the key to be omitted entirely (not null/empty) when unavailable,
      // and only honors it on the first accepted singular set_list — see IFRAME_KIT.md.
      ...(customerNumber != null ? { customerNumber: String(customerNumber) } : {}),
    });

    iFrameMessageHandlerRef.current.sendMessage(OPEN_CREATE_DIRECT_MAIL);
  };

  // Initializing the IFrame Mailer
  useEffect(() => {
    if (!isIFrameReady || !ballpointAPIKey || !directMailCampaigns
      || !primaryUserId || (!marketingProfile || Object.keys(marketingProfile).length === 0)
      || !profile?.type || isSenderSetupPendingRef.current) return;

    iFrameMessageHandlerRef.current.sendMessage(SET_API_CONFIG,
      { 
        apiBaseUrl: BALLPOINT_API_BASE_URL,
        apiToken: ballpointAPIKey,
        tenantKey: String(primaryUserId)
      });

    iFrameMessageHandlerRef.current.sendMessage(SET_SENDER, {
      ...convertMarketingProfileToSenderSnapshotJson(marketingProfile),
      externalUserIsAccountOwner: profile.type === 'FULL',
    });

    iFrameMessageHandlerRef.current.sendMessage(SET_DASHBOARD_FILTER,
      {
        listIds: directMailCampaigns?.length
          ? directMailCampaigns.map((campaign) => String(campaign.id)) : []
      });

    // Deep-link path: user arrived from an access point that pre-selected a campaign.
    // Seed the iframe with that specific list and open the create-campaign flow directly,
    // skipping the dashboard view.
    if (openCreateCampaignIFramePage && campaignForCreateCampaignPage) {
      if (campaignForCreateCampaignPage.id === createdCampaignIdRef.current) return;

      openCreateCampaignFlow(campaignForCreateCampaignPage)
        .catch((e) => console.error(e))
        .finally(() => stopLoader());
    }
    else {
      iFrameMessageHandlerRef.current.sendMessage(OPEN_DIRECT_MAIL_DASHBOARD);
      setPendingNewCampaign(null);
    }
  }, [isIFrameReady, ballpointAPIKey, directMailCampaigns, primaryUserId, marketingProfile, profile]);

  useEffect(() => {
    if (
      isIFrameReady &&
      isSenderSetupPendingRef.current &&
      marketingProfile &&
      Object.keys(marketingProfile).length > 0
    ) {
      iFrameMessageHandlerRef.current.sendMessage(
        SET_SENDER,
        {
          ...convertMarketingProfileToSenderSnapshotJson(marketingProfile),
          externalUserIsAccountOwner: profile.type === 'FULL',
        }
      );

      isSenderSetupPendingRef.current = false;

      closeEditMarketingProfilePopup();
    }
  }, [marketingProfile, isIFrameReady]);

  if (!isActiveInstance) return null;

  return (
    <>
      <div className={css.container}>
        <div className={classNames(css2.header, css.header)}>
          <div className={css2.left}>
            <div className={css.title}>
              <span title={"Direct Mail"} className={css.campaignName}>{"Direct Mail"}</span>
            </div>
          </div>
        </div>
        <div className={classNames(css2.content, css.body)}>
          <iframe
            key={iframeKey}
            id={iFrameId}
            className={css.iFrame}
            src={`${BALLPOINT_IFRAME_URL}/index.html?debug=1&${!openCreateCampaignIFramePage ? `externalUserId=${primaryUserId}` : ''}`}
            sandbox={"allow-scripts allow-same-origin allow-forms allow-downloads"}
          />
        </div>
      </div>
      <CampaginPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        iFrameMessageHandlerRef={iFrameMessageHandlerRef}
        isLoading={isPaymentLoading}
      />

      <LoaderModal isOpen={showLoader} />
    </>
  );
};

export default withRouter(
  connect(null, {
    openContactAssignmentPopup: ContactAssignmentPopup.open,
    EditMarketingProfilePopup: EditMarketingProfilePopup.open,
    closeEditMarketingProfilePopup: EditMarketingProfilePopup.close,
    openAddToMarketingListModal: AddToMarketingListModal.open,
    openSkipTraceModal: SkipTraceModal.open,
    openInfoAlert: InfoAlert.open,
  })(DirectMailCampaign)
);
